import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Layout } from "./components/Layout";
import SessionTimeout from "./components/SessionTimeout";

// Pages
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Bicycles from "./pages/Bicycles";
import BicycleDetail from "./pages/BicycleDetail";
import Models from "./pages/Models";
import Tallas from "./pages/Tallas";
import Materials from "./pages/Materials";
import Users from "./pages/Users";
import SoldBicycles from "./pages/SoldBicycles";
import SoldPendingCollection from "./pages/SoldPendingCollection";
import ShopList from "./pages/shop/ShopList";
import ShopDetail from "./pages/shop/ShopDetail";
import ShopReservations from "./pages/ShopReservations";
import SecondHand from "./pages/SecondHand";
import ShowRoom from "./pages/ShowRoom";
import InternalOrders from "./pages/InternalOrders";
import GeneralExpenses from "./pages/GeneralExpenses";
import Messages from "./pages/Messages";
import Inventory from "./pages/Inventory";
import PendingPayments from "./pages/PendingPayments";
import Commissions from "./pages/Commissions";
import DailyCash from "./pages/DailyCash";
import Calendar from "./pages/Calendar";
import Personal from "./pages/Personal";
import SuperAdminPanel from "./pages/SuperAdminPanel";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import Accesorios from "./pages/Accesorios";
import NombreLabels from "./pages/NombreLabels";
import BrotherLabels from "./pages/BrotherLabels";
import Proforma from "./pages/Proforma";
import AccesorioDetail from "./pages/AccesorioDetail";

// Pending User Page
function PendingPage() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center max-w-md p-8 bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-slate-800 mb-2">Cuenta pendiente de activación</h2>
        <p className="text-slate-500 text-sm mb-1">
          Tu cuenta <strong>{user?.email}</strong> ha sido registrada correctamente.
        </p>
        <p className="text-slate-500 text-sm mb-6">
          Un administrador debe asignarte una empresa antes de que puedas acceder al sistema.
        </p>
        <button
          onClick={() => { logout(); window.location.href = '/login'; }}
          className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors text-sm"
          data-testid="pending-logout-btn"
        >
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({ children, adminOnly = false, moduleKey = null }) {
  const { user, loading, isAdmin, isPending, isModuleVisible } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner w-10 h-10" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (isPending) {
    return <PendingPage />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  // Block access to disabled modules via direct URL
  if (moduleKey && !isModuleVisible(moduleKey)) {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
}

// Public Route (redirect to home if logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner w-10 h-10" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/tienda" element={<ShopList />} />
      <Route path="/tienda/bici/:id" element={<ShopDetail />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Protected routes */}
      <Route 
        path="/" 
        element={
          <ProtectedRoute moduleKey="dashboard">
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/bicycles" 
        element={
          <ProtectedRoute moduleKey="bicycles">
            <Bicycles />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/bicycles/:id" 
        element={
          <ProtectedRoute moduleKey="bicycles">
            <BicycleDetail />
          </ProtectedRoute>
        } 
      />

      {/* Redirect old second-hand route to unified showroom */}
      <Route 
        path="/second-hand" 
        element={<Navigate to="/showroom" replace />}
      />

      <Route 
        path="/showroom" 
        element={
          <ProtectedRoute moduleKey="showroom">
            <ShowRoom />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/inventory" 
        element={
          <ProtectedRoute moduleKey="inventory">
            <Inventory />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/models" 
        element={
          <ProtectedRoute moduleKey="models">
            <Models />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/tallas" 
        element={
          <ProtectedRoute moduleKey="tallas">
            <Tallas />
          </ProtectedRoute>
        } 
      />

      {/* Admin only routes */}
      <Route 
        path="/sold" 
        element={
          <ProtectedRoute adminOnly moduleKey="sold">
            <SoldBicycles />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/tienda-interna" 
        element={
          <ProtectedRoute moduleKey="sold">
            <ShopList embedded />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/tienda-interna/bici/:id" 
        element={
          <ProtectedRoute moduleKey="sold">
            <ShopDetail embedded />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reservadas" 
        element={
          <ProtectedRoute adminOnly moduleKey="sold">
            <ShopReservations />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/sold-pending" 
        element={
          <ProtectedRoute adminOnly moduleKey="sold">
            <SoldPendingCollection />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/materials" 
        element={
          <ProtectedRoute adminOnly moduleKey="materials">
            <Materials />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/users" 
        element={
          <ProtectedRoute adminOnly moduleKey="users">
            <Users />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/internal-orders" 
        element={
          <ProtectedRoute moduleKey="internal-orders">
            <InternalOrders />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/general-expenses" 
        element={
          <ProtectedRoute adminOnly moduleKey="general-expenses">
            <GeneralExpenses />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/messages" 
        element={
          <ProtectedRoute moduleKey="messages">
            <Messages />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/pending-payments" 
        element={
          <ProtectedRoute moduleKey="pending-payments">
            <PendingPayments />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/commissions" 
        element={
          <ProtectedRoute moduleKey="commissions">
            <Commissions />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/daily-cash" 
        element={
          <ProtectedRoute moduleKey="daily-cash">
            <DailyCash />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/calendario" 
        element={<Navigate to="/personal" replace />}
      />

      <Route 
        path="/personal" 
        element={
          <ProtectedRoute moduleKey="personal">
            <Personal />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/accesorios" 
        element={
          <ProtectedRoute moduleKey="accesorios">
            <Accesorios />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/accesorios/:id" 
        element={
          <ProtectedRoute moduleKey="accesorios">
            <AccesorioDetail />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/super-admin" 
        element={
          <ProtectedRoute>
            <SuperAdminPanel />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/employee-dashboard" 
        element={
          <ProtectedRoute>
            <EmployeeDashboard />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/nombre-labels" 
        element={
          <ProtectedRoute moduleKey="nombre-labels">
            <NombreLabels />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/proforma" 
        element={
          <ProtectedRoute moduleKey="proforma">
            <Proforma />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/brother-labels" 
        element={
          <ProtectedRoute moduleKey="brother-labels">
            <BrotherLabels />
          </ProtectedRoute>
        } 
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SessionTimeout />
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
